import React, { useState } from 'react'
import { Form, FormItem, Input, InputNumber, Select, SubmitButton, Switch } from 'formik-antd'
import { Col, Button } from 'antd'
import { Formik } from 'formik'
import { Row } from 'react-display-flex'
import { FormikHelpers } from 'formik'

import { localeNames } from '../../../../../date/date-properties'
import { currencies } from '../../../../Tenants/codes'
import { filterProps } from '../../../../../components/forms/utils'
import { ProposedTenant, TenantFormValues } from '../../../models'
import { validationSchema, segmentCodeOptions } from './validation'

interface Props {
  initialValues: TenantFormValues
  tenant: ProposedTenant
  onSubmit: (values: TenantFormValues, { setSubmitting, resetForm, setErrors }: FormikHelpers<TenantFormValues>) => void
  onReject: () => void
}

export const InformationPanel = ({ tenant, initialValues, onSubmit, onReject }: Props) => {
  const [isRejecting, setIsRejecting] = useState(false)

  const onRejectClick = async () => {
    setIsRejecting(true)
    await onReject()
    setIsRejecting(false)
  }

  return (
    <Col xs={24}>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
        {({ values, setFieldValue }) => (
          <Form name="create-or-update-tenant">
            <FormItem name="customerName" label="Name" required>
              <Input name="customerName" placeholder="Customer name" />
            </FormItem>
            <FormItem name="abn" label="ABN" required>
              <Input name="abn" placeholder="ABN" />
            </FormItem>
            <FormItem name="domain" htmlFor="domain" label="Domain" required>
              <Input name="domain" placeholder="Tenant domain" prefix="https://" suffix=".imperium.markets" />
            </FormItem>
            <FormItem name="isDemo" label="Is Demo">
              <Switch checkedChildren="Yes" unCheckedChildren="No" name="isDemo" />
            </FormItem>
            <FormItem name="isTrial" label="Is Trial">
              <Switch checkedChildren="Yes" unCheckedChildren="No" name="isTrial" />
            </FormItem>
            <FormItem name="timezone" label="Timezone" required>
              <Select
                name="timezone"
                placeholder="Select the timezone"
                showSearch
                options={localeNames.map((timezone: string) => ({ label: timezone, value: timezone }))}
                {...filterProps}
              />
            </FormItem>
            <FormItem name="currency" label="Currency" required>
              <Select
                name="currency"
                placeholder="Select the currency"
                showSearch
                options={currencies.map((currency: string) => ({ label: currency, value: currency }))}
                {...filterProps}
              />
            </FormItem>
            <FormItem name="backOfficeEmail" label="Back Office Email">
              <Input name="backOfficeEmail" placeholder="Back Office Email" />
            </FormItem>
            <FormItem name="tPlusNDays" label="T+N">
              <InputNumber name="tPlusNDays" placeholder="Default T+N" min={0} parser={(value) => value || 0} />
            </FormItem>
            <FormItem name="basisPointFee" label="Basis Point Fee">
              <InputNumber name="basisPointFee" placeholder="0" min={0} max={100} parser={(value) => value || 0} />
            </FormItem>
            <FormItem label="Has Separation of Duties" name="groupDuties">
              <div className="switch-input-dependent">
                <FormItem name="enforceSeparationOfDuties">
                  <Switch checkedChildren="Yes" unCheckedChildren="No" name="enforceSeparationOfDuties" />
                </FormItem>
                <FormItem name="signOffCount" label="Signoff Count" className="sign-off-count">
                  <InputNumber
                    name="signOffCount"
                    placeholder="Signoff Count"
                    disabled={!values?.enforceSeparationOfDuties}
                    min={0}
                    max={10}
                    parser={(value) => value || 0}
                    value={values?.enforceSeparationOfDuties ? values?.signOffCount : 0}
                  />
                </FormItem>
              </div>
            </FormItem>
            <FormItem name="segmentCode" label="Segment Code" required>
              <Select
                placeholder="Select a segment code"
                name="segmentCode"
                showSearch
                options={segmentCodeOptions}
                {...filterProps}
              />
            </FormItem>
            <FormItem name="passwordExpiryMonths" label="Password Expiry">
              <InputNumber
                name="passwordExpiryMonths"
                placeholder="Password Expiry in Months"
                min={1}
                max={12}
                formatter={(value) => `${value} Months`}
              />
            </FormItem>
            <FormItem name="capitalBasisEnabled" label="Has Capital Basis">
              <Switch
                checkedChildren="Yes"
                unCheckedChildren="No"
                name="capitalBasisEnabled"
                data-testid="capitalBasis"
              />
            </FormItem>
            <FormItem name="longTermComplianceOnly" label="Has Long Term Compliance">
              <Switch checkedChildren="Yes" unCheckedChildren="No" name="longTermComplianceOnly" />
            </FormItem>
            {tenant?.acceptanceStatus === 'IN_PROGRESS' && (
              <Row className="action-buttons">
                <Button
                  onClick={onRejectClick}
                  loading={isRejecting}
                  className="reject-button"
                  htmlType="button"
                  type="default"
                >
                  Reject
                </Button>
                <SubmitButton htmlType="submit" type="primary">
                  {tenant?.status === 'DUPLICATE' ? 'Save' : 'Approve'}
                </SubmitButton>
              </Row>
            )}
          </Form>
        )}
      </Formik>
    </Col>
  )
}
